<template>
  <!-- TKP-220 (goreveposta.pdf): Görevler › Ayarlar › E-posta Ayarları -->
  <div data-test="mail-settings">
    <div class="mb-3">
      <h6>{{ $t("task.settings.mail.events") }}</h6>
      <multiselect
        :value="eventValue"
        :options="eventOptions"
        :multiple="true"
        :close-on-select="false"
        :searchable="false"
        :show-labels="false"
        :disabled="!canEdit"
        track-by="value"
        label="text"
        :placeholder="$t('task.settings.mail.events-placeholder')"
        data-test="mail-events"
        @input="onEvents"
      />
    </div>

    <div v-if="showRecipients" class="mb-3" data-test="mail-recipients-block">
      <h6>{{ $t("task.settings.mail.recipients") }} <span class="text-danger">*</span></h6>
      <multiselect
        :value="recipientValue"
        :options="recipientOptions"
        :multiple="true"
        :close-on-select="false"
        :searchable="false"
        :show-labels="false"
        :disabled="!canEdit"
        track-by="value"
        label="text"
        :placeholder="$t('task.settings.mail.recipients-placeholder')"
        :class="{ 'is-invalid': error === 'task.settings.mail.recipients-required' }"
        data-test="mail-recipients"
        @input="(v) => (form.recipients = (v || []).map((o) => o.value))"
      />
    </div>

    <div v-for="r in reminders" :key="r.key" class="d-flex align-items-center flex-wrap mb-2" :data-test="'mail-' + r.key">
      <b-form-checkbox v-model="form[r.key].enabled" switch :disabled="!canEdit" class="mr-2" :data-test="'mail-' + r.key + '-enabled'">{{ $t(r.label) }}</b-form-checkbox>
      <span class="small text-muted mr-2">{{ $t("task.settings.mail.days") }}:</span>
      <input
        type="text"
        class="form-control form-control-sm"
        style="max-width: 180px"
        :class="{ 'is-invalid': form[r.key].enabled && !form[r.key].days.length && error === 'task.settings.mail.days-required' }"
        :value="form[r.key].days.join(', ')"
        :disabled="!canEdit || !form[r.key].enabled"
        :placeholder="$t('task.settings.mail.days-placeholder')"
        :data-test="'mail-' + r.key + '-days'"
        @change="(e) => (form[r.key].days = parseDays(e.target.value))"
      />
      <span class="small text-muted ml-2">{{ $t("task.settings.mail.days-hint") }}</span>
    </div>

    <h6 class="mt-4">{{ $t("task.settings.mail.texts") }}</h6>
    <p class="small text-muted mb-2" data-test="mail-variables">
      {{ $t("task.settings.mail.texts-hint") }}
      <span v-for="v in variables" :key="v" class="mr-2"><code>{{ "{" + v + "}" }}</code> {{ $t("task.settings.mail.variable." + v) }}</span>
    </p>
    <b-card v-for="key in templateKeys" :key="key" no-body class="mb-1 shadow-none border" :data-test="'mail-template-' + key">
      <b-card-header class="py-1 px-2 hand d-flex align-items-center" @click="toggle(key)">
        <i :class="open === key ? 'bx bx-chevron-down' : 'bx bx-chevron-right'" class="mr-1"></i>
        <span class="small">{{ $t("task.settings.mail.template." + key) }}</span>
        <b-badge v-if="customized(key)" variant="info" class="ml-2" data-test="mail-template-custom">{{ $t("task.settings.mail.customized") }}</b-badge>
      </b-card-header>
      <b-collapse :visible="open === key">
        <b-card-body class="p-2">
          <input
            type="text"
            class="form-control form-control-sm mb-2"
            maxlength="200"
            :value="textOf(key, 'subject')"
            :placeholder="defaultOf(key, 'subject')"
            :disabled="!canEdit"
            :data-test="'mail-template-' + key + '-subject'"
            @input="(e) => setText(key, 'subject', e.target.value)"
          />
          <textarea
            class="form-control form-control-sm"
            rows="4"
            maxlength="5000"
            :value="textOf(key, 'body')"
            :placeholder="defaultOf(key, 'body')"
            :disabled="!canEdit"
            :data-test="'mail-template-' + key + '-body'"
            @input="(e) => setText(key, 'body', e.target.value)"
          ></textarea>
          <div v-if="canEdit && customized(key)" class="small text-muted mt-1">
            <!-- TKP-239 g.3: "Gri yazı varsayılan metindir." kaldırıldı -->
            <a href="javascript:;" :data-test="'mail-template-' + key + '-reset'" @click="reset(key)">{{ $t("task.settings.mail.reset") }}</a>
          </div>
        </b-card-body>
      </b-collapse>
    </b-card>

    <sweet-button v-if="canEdit" @click="save()" :loading="isUpdating" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-3" data-test="mail-save">{{ $t("common.save") }}</sweet-button>
  </div>
</template>

<script>
import { MAIL_EVENTS, MAIL_NONE, MAIL_RECIPIENTS, MAIL_TEMPLATE_KEYS, MAIL_VARIABLES, mailForm, mailFormError, mailPayload, needsRecipients, parseDays, pickEvents } from "./task-settings";

/**
 * TKP-220 — olaylar (çoklu, "Yok" tek başına), alıcılar (olay ya da hatırlatma varsa zorunlu), iki hatırlatma (aç/kapa + çok gün),
 * her olay ve hatırlatma için konu + gövde (boş = varsayılan; yer tutucu varsayılan metni gösterir). Yetki sunucuda.
 */
export default {
  props: { mail: { type: Object, default: null }, defaults: { type: Object, default: () => ({}) }, canEdit: { type: Boolean, default: false }, isUpdating: { type: Boolean, default: false } },
  data() {
    return { form: mailForm(this.mail), open: null, error: null, templateKeys: MAIL_TEMPLATE_KEYS, variables: MAIL_VARIABLES, reminders: [{ key: "dueReminder", label: "task.settings.mail.due-reminder" }, { key: "overdueReminder", label: "task.settings.mail.overdue-reminder" }] };
  },
  watch: {
    mail(v) {
      this.form = mailForm(v);
    },
  },
  computed: {
    eventOptions() {
      return [MAIL_NONE, ...MAIL_EVENTS].map((value) => ({ value, text: this.$t("task.settings.mail.event." + value) }));
    },
    eventValue() {
      const selected = this.form.events.length ? this.form.events : [MAIL_NONE];
      return this.eventOptions.filter((o) => selected.includes(o.value));
    },
    recipientOptions() {
      return MAIL_RECIPIENTS.map((value) => ({ value, text: this.$t("task.settings.mail.recipient." + value) }));
    },
    recipientValue() {
      return this.recipientOptions.filter((o) => this.form.recipients.includes(o.value));
    },
    showRecipients() {
      return needsRecipients(this.form);
    },
  },
  methods: {
    parseDays,
    onEvents(options) {
      const previous = this.form.events.length ? this.form.events : [MAIL_NONE];
      this.form.events = pickEvents(previous, (options || []).map((o) => o.value));
    },
    toggle(key) {
      this.open = this.open === key ? null : key;
    },
    textOf(key, field) {
      return (this.form.templates[key] && this.form.templates[key][field]) || "";
    },
    defaultOf(key, field) {
      return (this.defaults[key] && this.defaults[key][field]) || "";
    },
    setText(key, field, value) {
      this.form.templates = { ...this.form.templates, [key]: { ...(this.form.templates[key] || {}), [field]: value } };
    },
    customized(key) {
      const t = this.form.templates[key] || {};
      return !!(String(t.subject || "").trim() || String(t.body || "").trim());
    },
    reset(key) {
      const { [key]: _drop, ...rest } = this.form.templates;
      this.form.templates = rest;
    },
    async save() {
      this.error = mailFormError(this.form);
      if (this.error) {
        this.$toast.warning(this.$t(this.error));
        return;
      }
      try {
        await this.$store.dispatch("taskSettings/updateMail", mailPayload(this.form));
        this.$toast.success(this.$t("common.success"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>
